import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { logoutUser } from "../store/slices/authSlice";
import Sidebar from "./Sidebar";
import Header from "./Header";
import Footer from "./Footer";
import { useFooter } from "../contexts/FooterContext";
import { LIVE_NAME, LIVE_PATH, LEGACY_LIVE_PATH } from "../config/liveName";

/** Matches `Footer` fixed bar (~40.8px) plus a small gap so content is not covered. */
const FIXED_FOOTER_RESERVE_PX = 48;

/**
 * Main dashboard layout component with sidebar and header
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render in main content area
 */
const DashboardLayout = ({ children }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { isFooterVisible } = useFooter();
  useEffect(() => {
    if (!user) {
      window.location.href = "/login";
    }
  }, [user]);
  const location = useLocation();
  const isFlashbackPlayback = /^\/cameras\/\d+\/playback$/.test(location.pathname);
  const flashbackFillViewport =
    location.pathname === "/flashback" || isFlashbackPlayback;

  /**
   * Get current page name based on the current route
   */
  const getCurrentPageName = () => {
    const path = location.pathname;
    const routeMap = {
      "/dashboard": { heading: "Dashboard", subheading: "Dashboard" },
      "/test": { heading: "Test", subheading: "Test" },
      "/alert-services": {
        heading: "Alert Services",
        subheading: "Alert Services",
      },
      "/ai-detection": {
        heading: "AI Detection",
        subheading: "Use Cases & Configuration",
      },
      "/live-monitoring": {
        heading: "Live Monitoring",
        subheading: "Use Cases & Configuration",
      },
      [LIVE_PATH]: {
        heading: LIVE_NAME,
        subheading: LIVE_NAME,
      },
      [LEGACY_LIVE_PATH]: {
        heading: LIVE_NAME,
        subheading: LIVE_NAME,
      },
      "/digital-patrol": {
        heading: "Digital Patrol",
        subheading: "Digital Patrol",
      },
      "/ai-analysis": { heading: "AI Analysis", subheading: "AI Analysis" },
      "/flashback": { heading: "Flashback", subheading: "Flashback" },
      "/smart-reports": {
        heading: "Smart Reports",
        subheading: "Smart Reports",
      },
      "/administration": {
        heading: "Administration",
        subheading: "System administration",
      },
      "/nvr": { heading: "NVR", subheading: "Network Video Recorders" },
      "/user-management/users": {
        heading: "Users",
        subheading: "User Management",
      },
      "/administration/iot-devices-mgmt": {
        heading: "Administration",
        subheading: "IOT Devices",
      },
      "/administration/patrolling-route-mgmt": {
        heading: "Administration",
        subheading: "Patrolling Route Mgmt",
      },
      "/administration/camera-mgmt": {
        heading: "Administration",
        subheading: "Camera Management",
      },
      "/administration/zone-mgmt": {
        heading: "Administration",
        subheading: "Zone Management",
      },
      "/administration/ai-models-mgmt": {
        heading: "Administration",
        subheading: "AI Management",
      },
      "/administration/storage-mgmt": {
        heading: "Administration",
        subheading: "Storage Mgmt",
      },
      "/administration/user-mgmt": {
        heading: "Administration",
        subheading: "User Mgmt",
      },
      "/administration/patrol-settings": {
        heading: "Administration",
        subheading: "Patrol setting",
      },
      "/construction-site-management": {
        heading: "Construction Site Management",
        subheading: "Manage construction sites",
      },
    };

    if (routeMap[path]) {
      return routeMap[path];
    }

    if (isFlashbackPlayback) {
      return { heading: "Flashback", subheading: "Live view and recorded timeline" };
    }

    if (path.startsWith("/administration")) {
      return { heading: "Administration", subheading: "System administration" };
    }

    return {
      heading: "Live Monitoring",
      subheading: "Use Cases & Configuration",
    };
  };

  /**
   * Handle user logout
   */
  const handleLogout = async () => {
    await dispatch(logoutUser());
    window.location.replace("/login");
  };

  /**
   * Toggle sidebar collapsed state
   */
  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => !prev);
  };

  return (
    <div>
      <div className="dashboard-layout">
        <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />

        <div
          className={`main-content ${
            sidebarCollapsed ? "sidebar-collapsed" : ""
          }`}
        >
          <Header
            user={user}
            onLogout={handleLogout}
            onToggleSidebar={toggleSidebar}
            currentPage={getCurrentPageName()}
          />

          <main
            className={[
              "content-area",
              flashbackFillViewport ? "content-area--fill-viewport" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            style={{
              paddingBottom: isFooterVisible ? FIXED_FOOTER_RESERVE_PX : 0,
              boxSizing: "border-box",
            }}
          >
            {children}
          </main>
        </div>
      </div>
      {isFooterVisible && <Footer />}
    </div>
  );
};

export default DashboardLayout;
